(() => {
  const CUSTOM_ELEMENT = 'delivery-calculator-widget';
  const SESSION_GLOBAL_KEY = '__cpapEuropaDeliveryWidgetShown';
  const DAY_MS = 24 * 60 * 60 * 1000;
  const COOKIEBOT_SELECTORS = [
    '#CybotCookiebotDialog',
    '#CybotCookiebotDialogBody',
    '.CybotCookiebotDialog',
    '[data-cookiebanner]',
    '[data-cookie-banner]'
  ];

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function toPositiveNumber(value, fallback) {
    const number = toNumber(value, fallback);
    return number >= 0 ? number : fallback;
  }

  function readCookie(name) {
    const encodedName = `${encodeURIComponent(name)}=`;
    return document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(encodedName)) || '';
  }

  function writeCookie(name, value, days) {
    const expiresAt = new Date(Date.now() + days * DAY_MS).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expiresAt}; path=/; SameSite=Lax${secure}`;
  }

  function setLocalSuppression(key, days) {
    try {
      window.localStorage.setItem(key, String(Date.now() + days * DAY_MS));
    } catch (error) {
      
    }
  }

  function hasLocalSuppression(key) {
    try {
      const expiresAt = Number(window.localStorage.getItem(key));
      if (!expiresAt) return false;

      if (expiresAt > Date.now()) return true;

      window.localStorage.removeItem(key);
    } catch (error) {
      return false;
    }

    return false;
  }

  function hasSessionSeen(key) {
    if (window[SESSION_GLOBAL_KEY]) return true;

    try {
      return window.sessionStorage.getItem(key) === '1';
    } catch (error) {
      return false;
    }
  }

  function markSessionSeen(key) {
    window[SESSION_GLOBAL_KEY] = true;

    try {
      window.sessionStorage.setItem(key, '1');
    } catch (error) {

    }
  }

  function isVisible(element) {
    if (!element || !element.isConnected) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  function isCookieBannerVisible() {
    return COOKIEBOT_SELECTORS.some((selector) => Array.from(document.querySelectorAll(selector)).some(isVisible));
  }

  function getZonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);

    return parts.reduce((result, part) => {
      if (part.type !== 'literal') result[part.type] = Number(part.value);
      return result;
    }, {});
  }

  function wallTimeToDate(parts, timeZone) {
    const wallTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second || 0
    );
    let guess = wallTime;

    for (let index = 0; index < 3; index += 1) {
      const zonedParts = getZonedParts(new Date(guess), timeZone);
      const zonedTime = Date.UTC(
        zonedParts.year,
        zonedParts.month - 1,
        zonedParts.day,
        zonedParts.hour,
        zonedParts.minute,
        zonedParts.second || 0
      );
      const offset = zonedTime - guess;
      const nextGuess = wallTime - offset;

      if (Math.abs(nextGuess - guess) < 1000) break;
      guess = nextGuess;
    }

    return new Date(guess);
  }

  function addBusinessDays(parts, days) {
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    };
  }

  function getCutoffInfo(now, timeZone, cutoffHour, cutoffMinute) {
    const businessNow = getZonedParts(now, timeZone);
    const todayCutoff = wallTimeToDate({
      year: businessNow.year,
      month: businessNow.month,
      day: businessNow.day,
      hour: cutoffHour,
      minute: cutoffMinute,
      second: 0
    }, timeZone);

    if (now.getTime() <= todayCutoff.getTime()) {
      return {
        todayCutoff,
        nextCutoff: todayCutoff,
        hasPassedToday: false
      };
    }

    const tomorrow = addBusinessDays(businessNow, 1);
    const nextCutoff = wallTimeToDate({
      year: tomorrow.year,
      month: tomorrow.month,
      day: tomorrow.day,
      hour: cutoffHour,
      minute: cutoffMinute,
      second: 0
    }, timeZone);

    return {
      todayCutoff,
      nextCutoff,
      hasPassedToday: true
    };
  }

  function formatLocalTime(date) {
    const locale = document.documentElement.lang || window.Shopify?.locale || navigator.language || 'en-US';

    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).format(date);
  }

  class DeliveryCalculatorWidget extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.handleDismiss = this.handleDismiss.bind(this);
      this.handleCta = this.handleCta.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.cookiebotListeners = [];
      this.cookiebotResolved = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.closeButton = this.querySelector('[data-delivery-widget-close]');
      this.ctaButton = this.querySelector('[data-delivery-widget-cta]');

      this.closeButton?.addEventListener('click', this.handleDismiss);
      this.ctaButton?.addEventListener('click', this.handleCta);
      this.addEventListener('keydown', this.handleKeydown);

      if (!this.updateCutoffTime()) return;
      if (!this.canShow()) return;

      this.waitForCookieBanner().then(() => {
        this.showTimer = window.setTimeout(() => {
          this.show();
        }, this.getSeconds('showDelaySeconds', 2) * 1000);
      });
    }

    disconnectedCallback() {
      this.closeButton?.removeEventListener('click', this.handleDismiss);
      this.ctaButton?.removeEventListener('click', this.handleCta);
      this.removeEventListener('keydown', this.handleKeydown);
      window.clearTimeout(this.showTimer);
      this.cleanupCookiebotWait();
    }

    getSeconds(name, fallback) {
      return toPositiveNumber(this.dataset[name], fallback);
    }

    canShow() {
      if (isDesignMode()) return true;
      if (hasSessionSeen(this.dataset.sessionKey)) return false;

      return !this.hasSuppression(this.dataset.dismissKey) && !this.hasSuppression(this.dataset.ctaKey);
    }

    hasSuppression(key) {
      return Boolean(readCookie(key)) || hasLocalSuppression(key);
    }

    setSuppression(key, days) {
      const durationDays = toPositiveNumber(days, 0);
      if (!key || durationDays <= 0 || isDesignMode()) return;

      try {
        writeCookie(key, '1', durationDays);
      } catch (error) {

      }

      setLocalSuppression(key, durationDays);
    }

    updateCutoffTime() {
      const timeElement = this.querySelector('[data-delivery-widget-cutoff-time]');
      if (!timeElement) return true;

      try {
        const timeZone = this.dataset.businessTimeZone || 'Europe/Sofia';
        const cutoffHour = Math.min(Math.max(toNumber(this.dataset.cutoffHour, 17), 0), 23);
        const cutoffMinute = Math.min(Math.max(toNumber(this.dataset.cutoffMinute, 0), 0), 59);
        const cutoffInfo = getCutoffInfo(new Date(), timeZone, cutoffHour, cutoffMinute);
        const hideAfterCutoff = this.dataset.hideAfterCutoff === 'true';

        if (hideAfterCutoff && cutoffInfo.hasPassedToday && !isDesignMode()) {
          return false;
        }

        const cutoff = hideAfterCutoff ? cutoffInfo.todayCutoff : cutoffInfo.nextCutoff;
        timeElement.textContent = formatLocalTime(cutoff);
      } catch (error) {
        const cutoffHour = String(Math.min(Math.max(toNumber(this.dataset.cutoffHour, 17), 0), 23)).padStart(2, '0');
        const cutoffMinute = String(Math.min(Math.max(toNumber(this.dataset.cutoffMinute, 0), 0), 59)).padStart(2, '0');
        timeElement.textContent = `${cutoffHour}:${cutoffMinute}`;
      }

      return true;
    }

    waitForCookieBanner() {
      if (isDesignMode()) return Promise.resolve();

      const minimumWait = this.getSeconds('initialWaitSeconds', 10) * 1000;
      let minimumWaitComplete = minimumWait <= 0;
      let sawCookieBanner = false;

      return new Promise((resolve) => {
        const finish = () => {
          if (this.cookiebotResolved) return;
          this.cookiebotResolved = true;
          this.cleanupCookiebotWait();
          resolve();
        };

        const check = () => {
          const bannerVisible = isCookieBannerVisible();
          if (bannerVisible) {
            sawCookieBanner = true;
            return;
          }

          if (minimumWaitComplete || sawCookieBanner) {
            finish();
          }
        };

        const onDialogDisplay = () => {
          sawCookieBanner = true;
          check();
        };
        const onConsent = () => {
          sawCookieBanner = true;
          window.setTimeout(check, 250);
        };

        [
          ['CookiebotOnDialogDisplay', onDialogDisplay],
          ['CookiebotOnAccept', onConsent],
          ['CookiebotOnDecline', onConsent],
          ['CookiebotOnConsentReady', check]
        ].forEach(([eventName, handler]) => {
          window.addEventListener(eventName, handler);
          this.cookiebotListeners.push([eventName, handler]);
        });

        this.cookiebotPoll = window.setInterval(check, 250);
        this.cookiebotMinimumTimer = window.setTimeout(() => {
          minimumWaitComplete = true;
          check();
        }, minimumWait);

        check();
      });
    }

    cleanupCookiebotWait() {
      window.clearInterval(this.cookiebotPoll);
      window.clearTimeout(this.cookiebotMinimumTimer);

      this.cookiebotListeners.forEach(([eventName, handler]) => {
        window.removeEventListener(eventName, handler);
      });
      this.cookiebotListeners = [];
    }

    show() {
      if (!this.isConnected || (!isDesignMode() && !this.canShow())) return;

      if (!isDesignMode()) {
        markSessionSeen(this.dataset.sessionKey);
      }

      this.hidden = false;
      window.requestAnimationFrame(() => {
        this.classList.add('is-visible');
      });
    }

    hide() {
      this.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!this.classList.contains('is-visible')) {
          this.hidden = true;
        }
      }, 200);
    }

    handleDismiss() {
      this.setSuppression(this.dataset.dismissKey, this.dataset.dismissDays);
      this.hide();
    }

    handleCta() {
      this.setSuppression(this.dataset.ctaKey, this.dataset.ctaSuppressDays);
      this.hide();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || this.hidden) return;

      event.preventDefault();
      this.handleDismiss();
    }
  }

  if (!customElements.get(CUSTOM_ELEMENT)) {
    customElements.define(CUSTOM_ELEMENT, DeliveryCalculatorWidget);
  }
})();
